import { XIcon } from '@phosphor-icons/react';
import * as React from 'react';

import { CircleIconButton } from '@/components/ui/circle-icon-button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';

interface FullScreenSplitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Page title shown in the top bar after the X + divider. */
  title: string;
  /**
   * Platform identity badge (e.g. `<PlatformIdentityBadge platform="google-ads" />`)
   * rendered in the top bar immediately before the title, persistent across every
   * step of the flow. Matches the dialog-header placement used by the Meta campaign
   * dialog — render it here rather than re-declaring it per step in `left`.
   */
  titleBadge?: React.ReactNode;
  /**
   * The surface's PLACE in the app, rendered where the plain `<h1>` would go —
   * and replacing it, so the leaf name is never on screen twice.
   *
   * 🔴 PASS `<RouteBreadcrumb />`, NOT A HAND-ROLLED RUN OF LINKS AND SLASHES.
   * This slot exists because both Brief surfaces smuggled a crumb through
   * `titleBadge` — a slot documented for a platform identity badge — and each
   * approximated the app's crumb differently. Founder, 2026-08-27, after the
   * dialog shipped: _"even the breadcrumb is inconsistent with other pages and
   * our page-header manager for full dialogs too."_ He was right, and it was
   * measurable: the hand-rolled root inherited the top bar's raw body text
   * (16px/400/-0.4px) while every crumb `PageHeaderManager` renders is
   * 14px/500/-0.35px, and the gap either side of the `/` was 6px and 16px
   * instead of 6 and 6 — because the root sat inside `titleBadge` and the leaf
   * was the `<h1>` beside it, two children of a `gap-3` row rather than two
   * items of one list. Neither hand-rolled crumb rendered a single `<li>`
   * inside a `<nav aria-label="breadcrumb">`, so assistive tech saw no
   * breadcrumb at all on either.
   *
   * `title` is still REQUIRED when this is set — it feeds the `sr-only`
   * `DialogTitle`, which is the dialog's accessible name and must not become a
   * breadcrumb path read aloud on every open.
   *
   * A dialog whose top bar names a TASK rather than a place ("New Meta
   * campaign") passes no breadcrumb and keeps the plain `<h1>`; there is no
   * hierarchy for a crumb to state.
   */
  breadcrumb?: React.ReactNode;
  /**
   * A short status shown right after the title, at every width: a stage dot and word for the object
   * the dialog is about. It is not a place to put controls or a second name.
   */
  titleSuffix?: React.ReactNode;
  /** Hidden description for screen readers. Defaults to title. */
  description?: string;
  /** Header actions (secondary + primary actions). Right-aligned, hidden on <sm. */
  headerActions?: React.ReactNode;
  /**
   * Top-bar controls that belong to the SURFACE rather than to a form — a
   * reporting-window picker, an overflow menu. Unlike `headerActions` these stay
   * visible at every width, because a control that governs what the whole
   * surface is showing must not disappear on a phone.
   */
  headerAside?: React.ReactNode;
  /** Main pane (form / editor / tabbed content). Scrollable, flex-1. */
  left: React.ReactNode;
  /**
   * Nested authoring Dialogs/Sheets that must remain descendants of this
   * dialog's dismissable layer. Use this instead of rendering a sibling beside
   * the route-owned shell. Each nested primitive must portal into the nearest
   * `DialogContent` container rather than the document body.
   */
  nestedDialogs?: React.ReactNode;
  /** Aside pane (live preview, or a facts rail). Scrollable, fixed width. Optional. */
  right?: React.ReactNode;
  /** Accessible label for the optional aside pane. */
  rightAriaLabel?: string;
  /** Aside width override. Default `lg:w-[480px] xl:w-[540px]`. Applies on either side. */
  rightClassName?: string;
  /**
   * Enables the canonical desktop-only draggable right rail. On mobile the
   * authoring caller keeps its Sheet, rather than turning a narrow screen into
   * a nested resizable layout.
   */
  rightResizable?: boolean;
  /**
   * Which side the fixed-width `right` pane sits on. Default `'right'`.
   *
   * `'left'` exists for reporting surfaces whose aside is an identity/facts
   * rail rather than a preview: a rail is what you read FIRST to know what you
   * are looking at, and left-to-right readers start there.
   *
   * 🔴 `'left'` MEANS *BEFORE*, AT EVERY WIDTH — including below `lg`, where
   * the panes stack and the aside is ordered above the main pane.
   *
   * This doc used to say the opposite ("a rail set to `'left'` still falls
   * BELOW the content on mobile — which is correct, because on a phone the
   * metrics are what the surface was opened for"), and it was wrong for the
   * one surface that uses it. `BriefDetailPage` is the only `asideSide="left"`
   * consumer in the repo (`rg asideSide src/`), its aside is a Details rail —
   * status, source, counts, objective — and the founder's own sentence about
   * why that layout works is _"there are the details sticky on the left, and
   * here are the ads."_ Stacking it last put ~2500px of Concept cards above
   * the one element he named, so a phone opened onto a dashed empty media slot
   * and nothing else. Measured at 390×844 before the change: the first
   * viewport held one 356×291 empty slot plus the stage rail.
   *
   * A preview pane (`'right'`, the default) is unaffected: it still stacks
   * BELOW, which is right, because you have to author something before there
   * is anything to preview.
   */
  asideSide?: 'left' | 'right';
  /**
   * How the shell meets the viewport. Default `'full-bleed'` — the shape every
   * authoring caller has always had: 100dvh x 100vw, radius 0, no border, the
   * scrim entirely hidden behind it.
   *
   * `'inset'` floats the same shell as a rounded, bordered card over the
   * dimmed app behind it, from `lg` up only. Below `lg` it is byte-identical to
   * `'full-bleed'`, because a 16px gutter around a 390px phone is 32px of
   * wasted width and a card edge nobody can act on.
   *
   * 🔴 WHY THIS IS A PROP AND NOT A SECOND SHELL. The founder, twice, on the
   * Brief page: _"look how marketer does it — they have this dialog."_ Put the
   * two captures side by side — `B1-PAIR-marketer-brief-panel-1680x894-light.jpg`
   * against `R1-B1V2-brief-detail-1440x900-light.png` — and the difference is
   * not the rail, the grid or the header, all of which already match. It is
   * that marketer's panel is a rounded card floating over a dimmed shell and
   * ours ran to the glass with radius 0 and no scrim, so it read as a page.
   * Two hard-coded lines were doing that: `rounded-none border-0` and
   * `sm:max-w-[100vw] sm:rounded-none`. Making them the DEFAULT branch of one
   * prop is a config extension on the canonical shell; forking a second
   * full-screen dialog for one caller is what this repo keeps paying for.
   *
   * THE COST, MEASURED BOTH WAYS ON THE LIVE ROUTE rather than estimated. An
   * earlier round declined this citing "34px of grid width"; the 34px is real
   * and the conclusion drawn from it was not. `/briefs/adbd9027-…` at 1440x900,
   * the same page rendered with the prop off and then on:
   *
   *   full-bleed  panel 1440x900  grid 1091px  columns 353 / 353 / 353
   *   inset       panel 1408x868  grid 1057px  columns 341.66 x 3
   *
   * Three columns either way, and every one still wider than marketer's own
   * fixed 330px cards. The whole price is 11.3px per card. The one scrolling
   * pane goes 1.36 -> 1.41 on 34px less height, against marketer's 1.61.
   *
   * Escape and the focus trap are UNCHANGED and deliberately so: marketer's
   * Escape is a no-op and their scrim swallows clicks, which is theirs being
   * worse, not theirs being different.
   */
  surface?: 'full-bleed' | 'inset';
  /**
   * Vertical alignment of the main pane's content. Default `'center'`, which is
   * right for a short form step that would otherwise strand ~500px of void
   * beneath it. Use `'start'` for tabbed or scrolling content, where centering
   * makes a short tab drift down the viewport and shift every time the reader
   * switches tabs.
   *
   * `'fill'` is for a main pane that carries its OWN top or bottom chrome — a
   * step ladder pinned to the top, a save bar pinned to the bottom. It makes
   * the content wrapper grow to the pane's full height, so those bars land on
   * the pane's edges instead of floating wherever the content happens to end.
   *
   * 🔴 WHY THIS IS NOT `'start'` WITH A TALLER CHILD. `'start'` leaves the
   * wrapper content-sized, and a `sticky bottom-0` bar inside a content-sized
   * box is a no-op: it is already at the bottom OF THE CONTENT. Measured on
   * `/briefs/:briefId/concepts/:id` at 1440x900 before this branch existed —
   * pane 809px tall, content 564px, save bar stranded at y=524 with ~285px of
   * void beneath it, which reads exactly like the half-loaded page the
   * `'center'` branch was added to prevent
   * (`B2-AFTER-mora-02-ad-editor-same-shell-1440x900-dark.png`, first cut).
   *
   * No `min-h-0`: a flex item's `min-height: auto` is what lets tall content
   * push past the pane and hand the scroll to the pane's own `overflow-y-auto`
   * instead of being clipped. Adding `min-h-0` here would trade the void for a
   * silently truncated form, which is worse.
   */
  contentAlign?: 'center' | 'start' | 'fill';
  /** Grows the Close circle to a 44px target below md, for a surface people read on a phone. */
  touchClose?: boolean;
}

/**
 * Mora's canonical full-screen split dialog (Mercury / Talvo pattern).
 *
 *   ┌─ Top bar (border-b) ─────────────────────────────────────────────┐
 *   │ ✕ │ Title                                  [Cancel] [Primary CTA] │
 *   ├─ Body ─────────────────────────────────┬─────────────────────────┤
 *   │ left (flex-1, scroll)                  │ right (480-540px,       │
 *   │   forms, sectioned content             │   live preview)         │
 *   │   max-w-[600px] mx-auto recommended    │                         │
 *   └─────────────────────────────────────────┴─────────────────────────┘
 *
 * Use for: authoring tasks (>30s), in-context preview surfaces, and any surface
 * a reader WORKS INSIDE for minutes — a campaign detail with tabs, drill-down
 * and a nested inspector qualifies.
 * Don't use for: quick consumption modals, image/video studio (single asset focus).
 *
 * ⚠️ "Don't use for quick consumption modals" once got read as "don't use for
 * anything read-only", which is not what it says and led to campaign detail
 * being built as a plain routed page with a hand-matched width while the
 * campaign AUTHORING flow next door used this shell — two shapes for two verbs
 * on the same object. The line excludes a surface you glance at and dismiss,
 * not one you spend minutes in. Judge by dwell time, not by read-vs-write.
 *
 * Mobile (<lg): the panes stack in `asideSide` order — a default `'right'`
 * preview stacks BELOW the main pane, an `asideSide="left"` facts rail stacks
 * ABOVE it. Both scroll the body.
 * The shell hides shadcn's default close button via [&>button]:hidden — call
 * sites use the X icon in the top bar instead.
 */
export function FullScreenSplitDialog({
  open,
  onOpenChange,
  title,
  titleBadge,
  breadcrumb,
  titleSuffix,
  description,
  headerActions,
  headerAside,
  left,
  nestedDialogs,
  right,
  rightAriaLabel,
  rightClassName,
  rightResizable = false,
  asideSide = 'right',
  surface = 'full-bleed',
  contentAlign = 'center',
  touchClose = false,
}: FullScreenSplitDialogProps): JSX.Element {
  const canResizeRightRail = useMediaQuery('(min-width: 1024px)');
  /**
   * ONE main pane, parameterised by the divider it wears — never two JSX
   * blocks.
   *
   * 🔴 This was two: a `leftPane` const for the resizable branch and a
   * hand-copied `<div>` inline in the plain branch below, each carrying its own
   * `asideSide === 'left' && 'order-2'`. That is two expressions for one rule,
   * which is exactly how the Details rail and the stage chip on this shell's
   * only consumer drifted 300px apart and printed different numbers. The rule
   * that stacks an `asideSide="left"` rail FIRST on a phone now exists once,
   * and `full-screen-split-dialog.test.tsx` asserts it through BOTH branches.
   *
   * `order-2`, not `lg:order-2`: an aside placed BEFORE stays before at every
   * width. The resizable branch passes no divider — `ResizableHandle` IS its
   * divider; the plain branch draws one on the edge facing the aside.
   */
  const renderMainPane = (dividerClass?: string) => (
    <div
      className={cn(
        'flex flex-1 flex-col overscroll-contain lg:overflow-y-auto',
        // `min-h-0` ONLY for `'fill'` (GOAL.md checklist C9, round 2): a flex
        // item's automatic minimum height stays `auto` (grows to fit its
        // content) unless its own `overflow` is non-`visible` — this wrapper's
        // overflow is `visible` below `lg` (only `lg:overflow-y-auto` sets
        // it), so with no explicit floor it grew past Body's already-correct,
        // `overflow-hidden`-bounded height instead of shrinking to it. `left`
        // (`PaidAdAuthoringFlow`)'s OWN `h-full` scroll region — the thing
        // that is actually supposed to scroll, per the comment on Body below —
        // then resolved its `100%` against an undefined height and never
        // engaged, so the extra content (including the sticky footer) was
        // simply clipped by Body instead of reachable — the round-2 review's
        // live-verified evidence: the Continue button sat ~105px below the
        // fold at 390×844 with no real scroll affordance to reach it (a
        // programmatic `scrollIntoView` could reach it; a real wheel/touch
        // scroll could not). Other callers keep
        // the old auto-height behaviour, since only `Body`'s `max-lg:overflow-y-auto`
        // (removed for `'fill'` only, see below) makes THIS wrapper's own
        // height matter to them below `lg`.
        contentAlign === 'fill' && 'min-h-0',
        // Stacked (below `lg`) with an aside: the main pane keeps its own height so the aside follows
        // it directly. `flex-1` stretched a short page to the viewport and parked the aside at the
        // bottom of the screen with a void above it. `'fill'` keeps `flex-1` because it needs the
        // bounded height; with no aside there is nothing to follow, so the pane still fills.
        right && contentAlign !== 'fill' && 'max-lg:flex-none',
        dividerClass,
        asideSide === 'left' && 'order-2',
      )}
    >
      <div
        className={cn(
          'w-full',
          contentAlign === 'center' && 'my-auto',
          // Same reasoning as the wrapper above: this div is ALSO a flex item
          // (of the wrapper above) whose own overflow is `visible`, so it
          // needs the same explicit floor to pass the real, bounded height
          // down to `left`'s own `h-full` instead of growing past it.
          contentAlign === 'fill' && 'flex min-h-0 flex-1 flex-col',
        )}
      >
        {left}
      </div>
    </div>
  );
  const rightPane = right ? (
    <aside
      aria-label={rightAriaLabel}
      className={cn(
        'shrink-0 overflow-y-auto bg-muted/15',
        'lg:w-[480px] xl:w-[540px]',
        // The stacked separator sits on whichever edge faces the main pane: a
        // trailing aside needs a top border, a LEADING one needs a bottom
        // border. Keeping `border-t` on a leading aside drew the rule against
        // the top bar and left the rail and the cards touching.
        asideSide === 'left'
          ? 'order-1 max-lg:border-b max-lg:border-border/40'
          : 'max-lg:border-t max-lg:border-border/40',
        rightClassName,
      )}
    >
      {right}
    </aside>
  ) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-slot="full-screen-split-dialog-content"
        className={cn(
          'flex h-[100dvh] w-screen flex-col gap-0 overflow-hidden rounded-none border-0 p-0',
          // The base DialogContent caps every dialog at `max-h-[calc(100vh-4rem)]`
          // so a tall one scrolls instead of clipping its head/foot (see that
          // token's own comment in dialog.tsx). This shell IS the full viewport —
          // that cap fights `h-[100dvh]` above and wins (tailwind-merge keeps the
          // later `max-h-*` class), leaving the dialog 64px short with the app
          // bleeding through the gap. Override it back to none.
          'max-h-none',
          'max-w-[100vw] sm:max-w-[100vw] sm:rounded-none',
          'top-0 left-0 translate-x-0 translate-y-0',
          // Animation: slide-up from bottom on open, fade-out on close.
          // Overrides shadcn's default zoom-95 (which is wrong for full-screen).
          // Keep the shell's transition opt-in explicit. The global reduced-
          // motion guard is still the safety net for older consumers, while
          // these variants make this canonical surface correct in isolation.
          'motion-safe:duration-300 motion-safe:ease-magnetic motion-reduce:duration-0 motion-reduce:transition-none',
          'motion-safe:data-[state=open]:animate-in motion-safe:data-[state=open]:fade-in-0 motion-safe:data-[state=open]:slide-in-from-bottom-4',
          'motion-safe:data-[state=closed]:animate-out motion-safe:data-[state=closed]:fade-out-0 motion-safe:data-[state=closed]:zoom-out-100 motion-safe:data-[state=closed]:slide-out-to-bottom-2',
          '[&>button]:hidden',
          // 🔴 THE PANEL EDGE — see the `surface` prop's docblock for why.
          // Every class here is `lg:`-gated and overrides one set above it, so
          // the `'full-bleed'` branch is byte-identical to what shipped before
          // and a phone never sees a gutter. Last in the list because that is
          // where tailwind-merge resolves a conflict in the author's favour;
          // written as ONE conditional string, not sprinkled fragments, so the
          // whole geometry of the inset panel reads in one place.
          surface === 'inset' &&
            // `shadow-elevation-3` is NOT repeated here: the base `DialogContent`
            // already carries it at every width (dialog.tsx:149), verified in the
            // rendered class list. Re-declaring it would read as this branch
            // adding an elevation it does not add.
            // 🔴 THE DARK EDGE IS A SEPARATE DECISION FROM THE LIGHT ONE, and
            // it is settled by measurement, not symmetry. The shared scrim
            // (`bg-black/40`, dialog.tsx:56) is not touched — it is every
            // dialog's, and this branch may not spend it.
            //
            // Sampled from the live route at 1440x900, panel fill against the
            // scrimmed surround 9px below the panel's bottom edge
            // (`B1V4-BEFORE-mora-brief-panel-1440x900-{light,dark}.png`):
            //
            //   light  panel rgb(253,253,253)  surround rgb(141,141,141)  3.263:1
            //   dark   panel rgb(20,20,22)     surround rgb(9,9,10)       1.082:1
            //
            // marketer holds ~1.44 (light) / ~1.49 (dark) on the same
            // measurement, and gets its dark half by INVERTING the scrim tint —
            // theirs is white at 20% over a black body, so their surround is
            // LIGHTER than their panel. We cannot do that from here.
            //
            // So the instrument that is in this branch's gift is the panel's own
            // edge, and the measurement says only DARK needs it: at 3.263 the
            // light panel already reads as a card and a brighter edge would only
            // ring it. `border-foreground/15` computes to ~rgb(51,51,53) against
            // that rgb(9,9,10) surround — a hairline near marketer's own panel
            // delta, on the one theme where `border-border/60` renders at
            // 1.0:1 and is literally invisible.
            'lg:top-4 lg:left-4 lg:h-[calc(100dvh-2rem)] lg:w-[calc(100vw-2rem)] lg:max-w-[calc(100vw-2rem)] lg:rounded-2xl lg:border dark:lg:border-foreground/15',
        )}
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">{description ?? title}</DialogDescription>

        {/* Top bar */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border/60 px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top,0px))] lg:px-6">
          <div className="flex min-w-0 items-center gap-3 lg:gap-4">
            <CircleIconButton
              size="sm"
              hitArea="touch"
              ariaLabel="Close"
              onClick={() => onOpenChange(false)}
              className={cn('shrink-0', touchClose && 'max-md:size-11')}
            >
              <XIcon className="size-4" weight="regular" />
            </CircleIconButton>
            <span aria-hidden className="h-5 w-px shrink-0 bg-border" />
            {titleBadge}
            {/* The crumb REPLACES the title rather than sitting beside it —
                otherwise the leaf and the `<h1>` both print the object's name
                between `sm` and `md`, which is the one thing the two Brief
                surfaces' hand-rolled headers were carefully avoiding by hand.
                `min-w-0` so a long name truncates inside the crumb instead of
                pushing `headerActions` off the bar. */}
            {breadcrumb ? (
              <div className="min-w-0">{breadcrumb}</div>
            ) : (
              <h1 className="hidden truncate text-sm font-medium tracking-tight text-foreground/85 sm:block">
                {title}
              </h1>
            )}
            {titleSuffix ? <div className="shrink-0">{titleSuffix}</div> : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {headerAside}
            {/* 🔴 NEVER `hidden sm:flex` HERE. These actions were desktop-only,
                and `NewWorkflowDialog`'s ONLY save path lives in this slot — so
                on a phone that dialog could be opened and filled in but never
                submitted. A capability may move (into a `⋯`, into a footer) but
                it may not silently disappear at a breakpoint; TOOLBAR-DOCTRINE
                §5 says a dropped tool stays reachable as a `⋯` row.

                Showing them is the right fix rather than adding a menu: this
                bar already hides its `<h1>` below `sm`, so the room exists, and
                the real consumer passes exactly two short buttons. If a future
                consumer passes enough actions to crowd a 390px bar, give THAT
                caller a `⋯` — do not re-hide the slot for everyone. */}
            {headerActions && <div className="flex items-center gap-2">{headerActions}</div>}
          </div>
        </div>

        {/* Body */}
        <div
          className={cn(
            'flex flex-1 overscroll-contain overflow-hidden max-lg:flex-col',
            // `contentAlign === 'fill'` (today: only `PaidAdAuthoringFlow`) means
            // the caller's OWN `left` content manages its scrolling and carries
            // its own `shrink-0` sticky footer, exactly like the `lg`+ branch
            // below already does — this wrapper's base `overflow-hidden` (no
            // `max-lg:` prefix) keeps it height-bounded at every width so that
            // chain works. The default branch instead lets THIS wrapper scroll
            // below `lg`, so a stacked aside pane can flow below the main pane
            // and the two scroll together as one page.
            //
            // Mixing the two below `lg` breaks the height chain a `'fill'`
            // caller depends on: `max-lg:overflow-y-auto` here made the WHOLE
            // Body auto-height, so `left`'s own `h-full` (built to fill this
            // wrapper) resolved against nothing definite, and its `shrink-0`
            // footer just flowed to the end of the (now unbounded) content
            // instead of staying pinned to the viewport's bottom edge —
            // live-verified: the paid campaign shell's Continue button sat
            // below the visible area at 390x844 (GOAL.md checklist C9).
            //
            // Scoped to `'fill'` because it is the one production caller today
            // (`rg contentAlign=\"fill\" src/` — the other hit is a doc comment,
            // not a real usage): a caller with a visible `right` pane on mobile
            // would need its own height budget inside a bounded Body, which is
            // out of scope until one exists.
            contentAlign === 'fill' ? undefined : 'max-lg:overflow-y-auto',
          )}
        >
          {/*
            The left pane centers its content vertically when the step is
            shorter than the viewport. A four-card objective step was anchoring
            to the top of a 900px pane and leaving ~500px of void beneath it,
            which reads as a page that failed to load rather than a deliberate
            step. `justify-center` with `my-auto` on the child keeps a TALL step
            scrolling normally from the top — centering must never clip the head
            of a long form.
          */}
          {rightResizable && rightPane && canResizeRightRail ? (
            <ResizablePanelGroup
              direction="horizontal"
              className="min-h-0 flex-1"
              data-slot="full-screen-split-resizable-body"
            >
              <ResizablePanel defaultSize="68" minSize="56" className="flex min-w-0 flex-col">
                {renderMainPane()}
              </ResizablePanel>
              <ResizableHandle withHandle />
              <ResizablePanel defaultSize="32" minSize="24" maxSize="44" className="min-w-0">
                {rightPane}
              </ResizablePanel>
            </ResizablePanelGroup>
          ) : (
            <>
              {renderMainPane(
                asideSide === 'right'
                  ? 'lg:border-r lg:border-border/60'
                  : 'lg:border-l lg:border-border/60',
              )}
              {rightPane}
            </>
          )}
        </div>
        {nestedDialogs}
      </DialogContent>
    </Dialog>
  );
}
